import { fmtBid } from "./leadUtils";
import type { DealLead } from "./types";

export type DetailRow = [string, unknown];
export type DetailSection = [string, DetailRow[]];

const money = (v: unknown): string | null =>
  v == null || v === "" ? null : fmtBid(Number(v));
const pct = (v: unknown): string | null =>
  v == null || v === "" ? null : `${Math.round(Number(v) * 100)}%`;
const date = (v: unknown): string | null => (v ? String(v).slice(0, 10) : null);
const join = (arr: unknown[], sep = " · "): string =>
  arr.filter((x) => x != null && x !== "").join(sep);

/** Keep only rows with a value. */
export const presentRows = (rows: DetailRow[]): DetailRow[] =>
  rows.filter((r) => r[1] != null && r[1] !== "");

/** The 6-section prospect card (Owner/Property/Valuation/Pre-Auction/Listing/Pipeline). */
export const prospectSections = (p: Record<string, any>): DetailSection[] => {
  const phones = (p.phones ?? [])
    .map((x: any) => x?.number)
    .filter(Boolean)
    .join(", ");
  const emails = (p.emails ?? [])
    .map((x: any) => x?.email)
    .filter(Boolean)
    .join(", ");
  return [
    [
      "Owner",
      [
        ["Owner", p.owner_name],
        ["Co-owner", p.co_owner_name],
        ["Phones", phones],
        ["Emails", emails],
        ["Occupancy", p.occupancy],
        [
          "Mailing",
          join(
            [p.mailing_address, p.mailing_city, p.mailing_state, p.mailing_zip],
            ", ",
          ),
        ],
      ],
    ],
    [
      "Property",
      [
        ["Address", p.property_address],
        [
          "City / State / ZIP",
          join([p.property_city, p.property_state, p.property_zip], ", "),
        ],
        ["County", p.county],
        ["APN", p.parcel_apn],
        ["Type", p.property_type],
        ["Beds / Baths / Sqft", join([p.beds, p.baths, p.sqft], " / ")],
        ["Year built", p.year_built],
        ["Lot acres", p.lot_acres],
        ["Stories", p.stories],
        ["Legal", p.legal_description],
      ],
    ],
    [
      "Valuation",
      [
        ["Tax value", money(p.tax_value)],
        ["Tax amount", money(p.tax_amount)],
        ["Tax year", p.tax_year],
        ["AVM", money(p.avm)],
        ["Est. equity", money(p.estimated_equity)],
        ["List price", money(p.list_price)],
        ["Sold amount", money(p.sold_amount)],
        ["Sold date", date(p.sold_date)],
        ["Original mortgage", money(p.original_mortgage)],
        ["Yrs owned", p.length_of_ownership_yrs],
      ],
    ],
    [
      "Pre-Auction",
      [
        ["Auction date", date(p.auction_date)],
        ["Auction type", p.auction_type],
        ["Auction status", p.auction_status],
        ["Opening bid", money(p.opening_bid)],
        ["Min bid", money(p.min_bid)],
        ["Lien amount", money(p.lien_amount)],
        ["Lien position", p.lien_position],
        ["Judgment", money(p.judgment_amount)],
        ["Plaintiff / lender", p.plaintiff_lender],
        ["Case #", p.case_number],
        ["Trustee", p.trustee],
        ["Sale location", p.sale_location],
        ["Redemption deadline", date(p.redemption_deadline)],
      ],
    ],
    [
      "Listing",
      [
        ["Status", p.listing_status],
        ["List date", date(p.list_date)],
        ["DOM", p.days_on_market],
        ["MLS ID", p.mls_id],
        ["Agent", p.list_agent],
        ["Office", p.list_office],
      ],
    ],
    [
      "Pipeline",
      [
        ["Stage", p.stage],
        ["Attempts", p.attempts],
        ["Last contact", date(p.last_contact_at)],
        ["Source", p.source],
      ],
    ],
  ];
};

/** Detail rows for a not-yet-imported lead, from the fresh Airtable record. */
export const buildDetailRows = (
  f: Record<string, any>,
  isAuction: boolean,
): DetailRow[] => {
  if (!isAuction) {
    return [
      ["Owner", f["Borrower / Owner"] || f["Decedent"] || f["Name"]],
      ["Address", f["Final Address"] || f["Address"]],
      ["City / State / ZIP", join([f["City"], f["State"], f["Zip"]], ", ")],
      ["County", f["County"]],
      ["Probate type", f["Probate Type"] || f["Type"]],
      ["Filing date", f["Filing Date"]],
      ["Est. value", money(f["ARV Estimate"] || f["Est Value"])],
      ["Deal tier", f["Deal Tier v3"]],
      ["Deal score", f["Deal Score v3"]],
      ["Legal description", f["Legal Description"]],
      ["Phones", join([f["Phone"], f["Phone 2"], f["Phone 3"]], ", ")],
    ];
  }
  return [
    ["Owner", f["Borrower / Owner"]],
    ["Address", f["Final Address"] || f["Address"]],
    ["City / State / ZIP", join([f["City"], f["State"], f["Zip"]], ", ")],
    ["County", f["County"]],
    ["Auction date", f["Sale Date"]],
    ["Auction type", f["Auction Type"]],
    ["Deal tier", f["Deal Tier v3"]],
    ["Deal score", f["Deal Score v3"]],
    ["Risk", f["Risk Display"] || f["Risk Tier"]],
    ["Opening bid", money(f["Opening Bid"])],
    ["Est. value (ARV)", money(f["ARV Estimate"])],
    ["Equity %", pct(f["Equity %"])],
    ["Est. net profit", money(f["Estimated Net Profit"])],
    ["Last list price", money(f["Last List Price"])],
    ["Last list date", f["Last List Date"]],
    ["Last sold price", money(f["Last Sold Price"])],
    ["Last sold date", f["Last Sold Date"]],
    ["Beds / Baths / Sqft", join([f["Beds"], f["Baths"], f["Sqft"]], " / ")],
    ["Year built", f["Year Built"]],
    ["Total known liens", money(f["Total Known Liens"])],
    ["Lien position", f["Lien Position"]],
    ["Plaintiff / lender", f["Plaintiff / Lender"]],
    ["Trustee", f["Trustee"]],
    ["Tax delinquent", f["Tax Delinquent"]],
    ["Tax amount owed", money(f["Tax Amount Owed"])],
    [
      "Phones",
      join([f["PHONE "], f["PHONE 2"], f["PHONE 3"], f["PHONE 4"]], ", "),
    ],
    ["Legal description", f["Legal Description"]],
  ];
};

/** Rows built from the list item alone (Airtable fetch failed). */
export const fallbackRows = (item: DealLead): DetailRow[] => {
  const isAuction = item.kind === "auction";
  return [
    ["Address", item.address],
    ["County / ZIP", join([item.county, item.zip])],
    [
      isAuction ? "Auction date" : "Filing date",
      isAuction ? item.saleDate : item.filingDate,
    ],
    [
      "Opening bid",
      isAuction && item.openingBid != null ? fmtBid(item.openingBid) : null,
    ],
    [
      "Est. value",
      !isAuction && item.estValue != null ? fmtBid(item.estValue) : null,
    ],
    ["Phones", item.phones?.length ? item.phones.join(", ") : null],
  ];
};
